import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { EnvelopeSimple, MapPin, Phone, TelegramLogo, DiscordLogo, TwitterLogo, CheckCircle } from '@phosphor-icons/react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate form submission
    setTimeout(() => {
      setSubmitted(true);
    }, 1000);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">
            Get in Touch
          </span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">
            Contact Us
          </h1>
          <p className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
            Have questions or need support? We're here to help. Reach out through any of the channels below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {/* Email */}
            <div className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
              <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center mb-4">
                <EnvelopeSimple size={24} className="text-[var(--color-primary)]" weight="bold" />
              </div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-1">Email</h3>
              <a href="mailto:support@defi-bot.io" className="text-[var(--text-secondary)] hover:text-[var(--color-primary)]">
                support@defi-bot.io
              </a>
            </div>

            {/* Office */}
            <div className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
              <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center mb-4">
                <MapPin size={24} className="text-[var(--color-primary)]" weight="bold" />
              </div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-1">Office</h3>
              <p className="text-[var(--text-secondary)]">
                71-75 Shelton Street<br />
                Covent Garden, London<br />
                WC2H 9JQ, United Kingdom
              </p>
            </div>

            {/* Phone */}
            <div className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
              <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center mb-4">
                <Phone size={24} className="text-[var(--color-primary)]" weight="bold" />
              </div>
              <h3 className="font-semibold text-[var(--text-primary)] mb-1">Phone</h3>
              <a href="tel:+442071234567" className="text-[var(--text-secondary)] hover:text-[var(--color-primary)]">
                +44 20 7123 4567
              </a>
            </div>

            {/* Social Links */}
            <div className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
              <h3 className="font-semibold text-[var(--text-primary)] mb-4">Follow Us</h3>
              <div className="flex gap-3">
                <a
                  href="https://t.me/ai_defibot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-[var(--bg-surface-2)] text-[var(--text-secondary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-all"
                >
                  <TelegramLogo size={24} weight="bold" />
                </a>
                <a
                  href="https://discord.gg/defibot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-[var(--bg-surface-2)] text-[var(--text-secondary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-all"
                >
                  <DiscordLogo size={24} weight="bold" />
                </a>
                <a
                  href="https://twitter.com/defibot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 rounded-xl bg-[var(--bg-surface-2)] text-[var(--text-secondary)] hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)]/10 transition-all"
                >
                  <TwitterLogo size={24} weight="bold" />
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="p-8 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]">
              {submitted ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-12"
                >
                  <CheckCircle size={64} className="text-[var(--accent-success)] mx-auto mb-4" weight="fill" />
                  <h3 className="text-2xl font-semibold text-[var(--text-primary)] mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-[var(--text-secondary)]">
                    We'll get back to you within 24 hours.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)]"
                        placeholder="Your name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)]"
                        placeholder="your@email.com"
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                      Subject
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] text-[var(--text-primary)] focus:outline-none focus:border-[var(--color-primary)]"
                      data-testid="contact-subject"
                    >
                      <option value="">Select a subject</option>
                      <option value="general">General Inquiry</option>
                      <option value="support">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="partnership">Partnership</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)] text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--color-primary)] resize-none"
                      placeholder="Your message..."
                      data-testid="contact-message"
                    />
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    type="submit"
                    className="w-full py-4 bg-[var(--color-primary)] text-[var(--bg-primary)] font-semibold rounded-xl hover:shadow-[0_0_20px_var(--border-glow)] transition-all"
                    data-testid="contact-submit"
                  >
                    Send Message
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
